import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';

export default class SiteHeader extends HTMLElement {
    constructor() {
        super();
        this.initElements();
        this.bindMethods();
        this.setupInitialStates();
    }

    initElements() {
        this.toggleNavLinks = this.querySelectorAll('[data-toggle-nav]');
        this.toggleSubnavs = this.querySelectorAll('ul.catalog-nav > li');
        this.nav = this.querySelector('nav.main-nav');
        this.toggleSearchLinks = this.querySelectorAll('[data-toggle-search]');
        this.searchOverlay = this.querySelector('.search-overlay');
        this.searchInput = this.searchOverlay.querySelector('input[type="search"]');
        this.userOverlay = this.querySelector('.user-overlay');
        this.toggleUserLinks = this.querySelectorAll('[data-toggle-user]');
    }

    bindMethods() {
        this.handleResize = this.handleResize.bind(this);
        this.handleScroll = this.handleScroll.bind(this);
        this.handleClickSubnav = this.handleClickSubnav.bind(this);
        this.handleMouseEnterSubnav = this.handleMouseEnterSubnav.bind(this);
        this.handleMouseLeaveSubnav = this.handleMouseLeaveSubnav.bind(this);
    }

    setupInitialStates() {
        this.navOpened = false;
        this.searchOpened = false;
        this.userOpened = false;

        this.currentScrollPosition = 0;
        this.previousScrollPosition = window.scrollY || document.documentElement.scrollTop;
        this.downThreshold = 200;
        this.upThreshold = 100;
        this.isHeaderHidden = false;

        this.backdrop = this.createBackdrop();

        this.subnavTimeouts = new Map();
    }

    connectedCallback() {
        this.setupEventListeners();
        this.handleResize();
    }

    disconnectedCallback() {
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('resize', this.handleResize);
        this.removeSubnavEventListeners();
    }

    setupEventListeners() {
        window.addEventListener('scroll', this.handleScroll);
        window.addEventListener('resize', this.handleResize);

        this.toggleNavLinks.forEach(link =>
            link.addEventListener('click', (e) => {
                e.preventDefault();
                this.toggleNav();
            })
        );

        this.toggleSearchLinks.forEach(link =>
            link.addEventListener('click', (e) => {
                e.preventDefault();
                this.toggleSearch();
            })
        );

        if (this.userOverlay) {
            this.toggleUserLinks.forEach(link =>
                link.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.toggleUser();
                })
            );
        }

        this.backdrop.addEventListener('click', () => {
            if (this.searchOpened) {
                this.toggleSearch();
            }
            if (this.userOpened) {
                this.toggleUser();
            }
        });
    }

    createBackdrop() {
        const backdrop = document.createElement('div');
        backdrop.classList.add('site-header-backdrop');
        this.insertAdjacentElement('afterend', backdrop);
        return backdrop;
    }

    toggleNav() {
        this.nav.classList.toggle('opened');
        this.navOpened = !this.navOpened;
        this.navOpened ? disableBodyScroll(this.nav) : clearAllBodyScrollLocks();
        this.classList.toggle('visible');

        if (this.searchOpened) this.toggleSearch();
    }

    toggleSearch() {
        if (this.navOpened) this.toggleNav();
        if (this.userOpened) this.toggleUser();

        this.searchOverlay.classList.toggle('opened');
        this.searchOpened = !this.searchOpened;

        if (this.searchOpened) this.searchInput.focus();

        this.backdrop.classList.toggle('active', this.searchOpened);
    }

    toggleUser() {
        if (this.navOpened) this.toggleNav();
        if (this.searchOpened) this.toggleSearch();

        if (this.userOverlay) {
            this.userOverlay.classList.toggle('opened');
            this.userOpened = !this.userOpened;
            this.backdrop.classList.toggle('active', this.userOpened);
        }

        this.toggleUserLinks.forEach(link =>
            link.classList.toggle('active', this.userOpened)
        );
    }

    handleScroll() {

        this.currentScrollPosition = window.scrollY || document.documentElement.scrollTop;
        const scrollDifference = this.currentScrollPosition - this.previousScrollPosition;

        if (scrollDifference > 0) {
            if (scrollDifference > this.downThreshold) {
                this.hideHeader();
                this.previousScrollPosition = this.currentScrollPosition;
            }
        } else if (scrollDifference < 0) {
            if (Math.abs(scrollDifference) > this.upThreshold) {
                this.showHeader();
                this.previousScrollPosition = this.currentScrollPosition;
            }
        }

        setTimeout(() => {
            this.handleResize();
        }, 200)

        if (this.searchOpened) this.toggleSearch();
        if (this.userOpened) this.toggleUser();
    }

    handleResize() {
        document.body.style.setProperty('--header-height', this.getBoundingClientRect().bottom + 'px');
        this.updateSubnavEventListeners();
    }

    updateSubnavEventListeners() {
        this.removeSubnavEventListeners();
        this.addSubnavEventListeners();
    }

    addSubnavEventListeners() {
        this.toggleSubnavs.forEach(toggleSubnav => {
            if (window.innerWidth < 1200) {
                toggleSubnav.addEventListener('click', this.handleClickSubnav);
            } else {
                toggleSubnav.addEventListener('mouseenter', this.handleMouseEnterSubnav);
                toggleSubnav.addEventListener('mouseleave', this.handleMouseLeaveSubnav);
            }
        });
    }

    removeSubnavEventListeners() {
        this.toggleSubnavs.forEach(toggleSubnav => {
            toggleSubnav.removeEventListener('click', this.handleClickSubnav);
            toggleSubnav.removeEventListener('mouseenter', this.handleMouseEnterSubnav);
            toggleSubnav.removeEventListener('mouseleave', this.handleMouseLeaveSubnav);
        });
    }

    handleClickSubnav(e) {
        if (!e.target.classList.contains('linkable')) {
            e.preventDefault();
        }
        e.currentTarget.classList.toggle('opened');
    }

    handleMouseEnterSubnav(e) {
        if (this.userOpened) {
            this.toggleUser();
        }

        const targetSubnav = e.currentTarget;

        const timeoutId = setTimeout(() => {
            if (this.userOpened) {
                this.toggleUser();
            }
            targetSubnav.classList.add('opened');
            this.backdrop.classList.add('active');
        }, 300);

        this.subnavTimeouts.set(targetSubnav, timeoutId);
    }

    handleMouseLeaveSubnav(e) {
        const targetSubnav = e.currentTarget;

        const timeoutId = this.subnavTimeouts.get(targetSubnav);
        if (timeoutId) {
            clearTimeout(timeoutId);
            this.subnavTimeouts.delete(targetSubnav);
        }

        e.currentTarget.classList.remove('opened');
        this.backdrop.classList.remove('active');
    }

    hideHeader() {
        if (!this.isHeaderHidden) {
            this.classList.add('hidden');
            this.isHeaderHidden = true;
        }
    }

    showHeader() {
        if (this.isHeaderHidden) {
            this.classList.remove('hidden');
            this.isHeaderHidden = false;
        }
    }
}
